import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api/client';

/* ───────────────────────────────────────────────────────────────────────────
   Contrato — GET /fiscal/accrual (FiscalAnalyticsController)

   Espelha `DetailedAccrualResult` (application/queries/get-detailed-accrual
   .query.ts). O motor tributário real roda de forma agregada sobre os
   documentos autorizados do período e apura obrigações por tributo.

   Query params (DetailedAccrualQueryDto):
     - companyId?, competencia? (YYYY-MM; padrão mês corrente), startDate?,
       endDate?, regime? (SIMPLES_NACIONAL|SIMPLES_NACIONAL_EXCESSO|
       SIMPLES_MEI|LUCRO_PRESUMIDO|LUCRO_REAL; padrão LUCRO_PRESUMIDO),
       originUf?, destinationUf?, operationType? (VENDA|COMPRA|...)

   O tenant vem da sessão.
   ─────────────────────────────────────────────────────────────────────────── */

export interface RevenueByCfop {
  cfop: string;
  base: number;
  documentCount: number;
}

export interface CalculatedObligation {
  taxType: string;
  label: string;
  baseValue: number;
  ratePct: number;
  grossTax: number;
  documentCount: number;
}

export interface DetailedAccrual {
  period: { startDate: string; endDate: string; competencia: string };
  revenue: {
    totalValue: number;
    authorizedValue: number;
    byCfop: RevenueByCfop[];
  };
  regimeSummary: {
    regime: string;
    documentsEvaluated: number;
    documentsAuthorized: number;
    companies: number;
    engineErrors: string[];
  };
  calculatedObligations: CalculatedObligation[];
  totals: { totalTaxes: number; effectiveRatePct: number };
  engineUsed: boolean;
  engineAudit: { evaluatedItems: number; totalRulesEvaluated: number };
}

export interface AccrualDetailQuery {
  competencia?: string;
  startDate?: string;
  endDate?: string;
  regime?: string;
  originUf?: string;
  destinationUf?: string;
  operationType?: string;
}

export function useAccrualDetail(params?: AccrualDetailQuery) {
  return useQuery({
    queryKey: ['fiscal', 'accrual', params] as const,
    queryFn: () =>
      apiClient.get<DetailedAccrual>('/fiscal/accrual', {
        params: {
          ...(params?.competencia ? { competencia: params.competencia } : {}),
          ...(params?.startDate ? { startDate: params.startDate } : {}),
          ...(params?.endDate ? { endDate: params.endDate } : {}),
          ...(params?.regime ? { regime: params.regime } : {}),
          ...(params?.originUf ? { originUf: params.originUf } : {}),
          ...(params?.destinationUf ? { destinationUf: params.destinationUf } : {}),
          ...(params?.operationType ? { operationType: params.operationType } : {}),
        },
      }),
  });
}